import type { TOC } from '@ember/component/template-only';
import {
  PrismButton,
  PrismCard,
  PrismMessage,
  PrismTable,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import GeneralDropdownsDropdownPageSize from 'lumen/components/general/dropdowns/dropdown-page-size';
import GeneralTablesPaginationFooter from 'lumen/components/general/tables/pagination-footer';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import ModalInviteUsers from 'lumen/components/settings/users/modals/modal-invite-users';
import BulkActionRow from 'lumen/components/settings/users/tables/bulk-action-row';
import type HotelSettingsUsersController from 'lumen/controllers/hotel/settings/users';

interface HotelSettingsUsersSignature {
  Args: {
    controller: HotelSettingsUsersController;
  };
}

const HotelSettingsUsers: TOC<HotelSettingsUsersSignature> = <template>
  <SettingsPageTemplate
    @description={{t "settings.users.description"}}
    @isFullHeight={{true}}
    @title={{t "settings.users.title"}}
    data-test-id="settings-users-page"
  >
    <:headerAfter>
      <div class="flex items-end gap-200 self-end">
        {{#if @controller.hasSelection}}
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.cancel"}}
            @onClick={{@controller.cancelSelection}}
          />
          <PrismButton
            @buttonType="primary"
            @isDisabled={{true}}
            @label={{t "settings.users.bulk.save"}}
          />
        {{else}}
          <PrismButton
            @buttonType="primary"
            @iconBefore="plus"
            @label={{t "settings.users.invite_users"}}
            @onClick={{@controller.openInviteModal}}
          />
        {{/if}}
      </div>
    </:headerAfter>

    <:default>
      <div class="flex h-full">
        <PrismCard @contentLayout="noPadding">
          <:content>
            <PrismTable
              @checkboxSelectionMode="multiple"
              @columns={{@controller.columns}}
              @isLoading={{@controller.isLoading}}
              @onRowSelect={{@controller.setRowSelection}}
              @onUpdateSorts={{@controller.onUpdateSorts}}
              @oneCellBulkEditRow={{false}}
              @rowSelection={{unless
                @controller.isLoading
                @controller.rowSelection
              }}
              @rows={{@controller.rows}}
              @sorts={{@controller.sorts}}
              @widthConstraint="gte-container-slack"
            >
              <:bulkEditRow as |bulkEditRow|>
                <BulkActionRow
                  @bulkBiBenchmarking={{@controller.bulkBiBenchmarking}}
                  @bulkRateInsight={{@controller.bulkRateInsight}}
                  @bulkRole={{@controller.bulkRole}}
                  @numberOfSelectedRows={{bulkEditRow.numberOfSelectedRows}}
                  @onBulkRoleChange={{@controller.handleBulkRoleChange}}
                />
              </:bulkEditRow>
              <:emptyState>
                <div class="flex h-full items-center justify-center">
                  <PrismMessage
                    @illustration="100x100_search"
                    @title={{t "settings.users.empty.nothing_matches_filters"}}
                  />
                </div>
              </:emptyState>
            </PrismTable>
          </:content>

          <:footer>
            {{#if @controller.totalRecords}}
              <GeneralTablesPaginationFooter
                @currentPage={{@controller.page}}
                @lastPage={{@controller.totalPages}}
                @onChangePageSize={{@controller.onChangePageSize}}
                @onSetPage={{@controller.setPage}}
                @pageSizeDropdown={{GeneralDropdownsDropdownPageSize}}
                @pageSizeOptions={{@controller.pageSizeOptions}}
                @pageSizeValue={{@controller.currentPageSizeOption}}
                @showingText={{@controller.showingText}}
              />
            {{/if}}
          </:footer>
        </PrismCard>
      </div>
    </:default>
  </SettingsPageTemplate>

  {{#if @controller.isInviteModalVisible}}
    <ModalInviteUsers @onClose={{@controller.closeInviteModal}} />
  {{/if}}
</template>;

export default HotelSettingsUsers;
